import { memo, useMemo } from 'react';
import { SidebarBoxKey } from 'AdmissionsGuidance';
import { type LearnerProject } from 'LearnerProjects';
import { type StreamForDashboard } from 'Lessons';
import { useTranslation } from 'react-i18next';
import { BasicMobileBox } from '../../BasicMobileBox';
import { type StatusConfig } from './StatusBox.types';
import { StatusBoxPresentation } from './StatusBoxPresentation';

type StatusBoxMobilePresentationProps = {
    statusConfig: StatusConfig;
    displayedCourses: StreamForDashboard[];
    displayedProjects: LearnerProject[];
    hasMoreCourses: boolean;
    hasMoreProjects: boolean;
    showAllCourses: boolean;
    showAllProjects: boolean;
    onToggleShowAllCourses: () => void;
    onToggleShowAllProjects: () => void;
    onCoursePress: (course: StreamForDashboard) => void;
    totalCoursesCount: number;
    totalProjectsCount: number;
    publishedStreamIdsWithAllContentStored: Record<string, boolean>;
};

/**
 * StatusBoxMobilePresentation - Mobile-specific wrapper
 */
const StatusBoxMobilePresentationComponent = ({
    statusConfig,
    displayedCourses,
    displayedProjects,
    hasMoreCourses,
    hasMoreProjects,
    showAllCourses,
    showAllProjects,
    onToggleShowAllCourses,
    onToggleShowAllProjects,
    onCoursePress,
    totalCoursesCount,
    totalProjectsCount,
    publishedStreamIdsWithAllContentStored,
}: StatusBoxMobilePresentationProps) => {
    const { t } = useTranslation('back_royal', { keyPrefix: 'statusBox' });
    const heading = useMemo(() => {
        const prefix = statusConfig.mobileTitlePrefix?.key
            ? t(statusConfig.mobileTitlePrefix.key, statusConfig.mobileTitlePrefix.params)
            : '';
        const title = statusConfig.mobileTitle?.key
            ? t(statusConfig.mobileTitle.key, statusConfig.mobileTitle.params)
            : '';
        return [prefix, title].filter(Boolean).join(' ');
    }, [statusConfig, t]);

    return (
        <BasicMobileBox
            boxKey={SidebarBoxKey.schedule_status}
            heading={heading}
            ctaText={t('viewDetails')}
            expandedContent={
                <StatusBoxPresentation
                    statusConfig={statusConfig}
                    displayedCourses={displayedCourses}
                    displayedProjects={displayedProjects}
                    hasMoreCourses={hasMoreCourses}
                    hasMoreProjects={hasMoreProjects}
                    showAllCourses={showAllCourses}
                    showAllProjects={showAllProjects}
                    onToggleShowAllCourses={onToggleShowAllCourses}
                    onToggleShowAllProjects={onToggleShowAllProjects}
                    onCoursePress={onCoursePress}
                    totalCoursesCount={totalCoursesCount}
                    totalProjectsCount={totalProjectsCount}
                    publishedStreamIdsWithAllContentStored={publishedStreamIdsWithAllContentStored}
                />
            }
        />
    );
};

export const StatusBoxMobilePresentation = memo(
    StatusBoxMobilePresentationComponent,
) as typeof StatusBoxMobilePresentationComponent;
